import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, uploadAsset } from "@/lib/api";
import { Plus, Trash2, Save, X, Upload, Image as ImageIcon, MapPin, Layers, CalendarDays, Pencil } from "lucide-react";
import GroupStandings from "@/components/admin/GroupStandings";

const FIELD_COLORS = ["#00F0FF", "#E5FF00", "#FF6600", "#FF2A55", "#22C55E", "#A855F7", "#0088FF", "#F97316"];

export default function TablesTab({ snapshot, view = "schedule" }) {
  const [teamName, setTeamName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef();
  const [editTeam, setEditTeam] = useState(null);
  const [teamConfirmDel, setTeamConfirmDel] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const editLogoRef = useRef();
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [fieldName, setFieldName] = useState("");
  const [standingsTitle, setStandingsTitle] = useState(snapshot.settings.standings_title || "Punktetabelle");
  const [scheduleTitle, setScheduleTitle] = useState(snapshot.settings.schedule_title || "Spielplan");

  useEffect(() => {
    setCols(snapshot.standings.columns || []);
    setRows(snapshot.standings.rows || []);
    // eslint-disable-next-line
  }, [snapshot.standings.columns.length, snapshot.standings.rows.length]);

  useEffect(() => {
    setStandingsTitle(snapshot.settings.standings_title || "Punktetabelle");
    setScheduleTitle(snapshot.settings.schedule_title || "Spielplan");
  }, [snapshot.active_project_id, snapshot.settings.standings_title, snapshot.settings.schedule_title]);

  const saveTitle = async (patch) => {
    try {
      await api.put("/settings", patch);
      toast.success("Überschrift gespeichert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const teams = snapshot.teams || [];
  const matches = snapshot.matches || [];
  const fields = snapshot.fields || [];
  const groups = snapshot.groups || [];
  const groupStandings = snapshot.group_standings || {};
  const matchdays = snapshot.matchdays || [];
  const fieldColors = Object.fromEntries(fields.map((f) => [f.name, f.color || "#00F0FF"]));
  const [groupName, setGroupName] = useState("");
  const [mdName, setMdName] = useState("");
  const [mdDate, setMdDate] = useState("");

  // ---- teams ----
  const addTeam = async () => {
    if (!teamName.trim()) return;
    setUploading(true);
    try {
      let logoUrl = null;
      if (logoFile) logoUrl = await uploadAsset(logoFile);
      await api.post("/teams", { name: teamName.trim(), logo: logoUrl });
      setTeamName("");
      setLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
      toast.success("Mannschaft hinzugefügt");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  const deleteTeam = async (id) => {
    await api.delete(`/teams/${id}`);
    toast.success("Mannschaft entfernt");
  };
  const changeTeamLogo = async (team, file) => {
    if (!file) return;
    try {
      const logoUrl = await uploadAsset(file);
      await api.put(`/teams/${team.id}`, { name: team.name, logo: logoUrl });
      toast.success("Logo aktualisiert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const openEdit = (t) => { setEditTeam({ id: t.id, name: t.name, logo: t.logo || null, color: t.color || "#00F0FF", short: t.short || "" }); setTeamConfirmDel(false); };
  const saveEdit = async () => {
    if (!editTeam.name.trim()) return;
    try {
      await api.put(`/teams/${editTeam.id}`, { name: editTeam.name.trim(), logo: editTeam.logo, color: editTeam.color, short: (editTeam.short || "").trim() });
      toast.success("Mannschaft gespeichert");
      setEditTeam(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editUploadLogo = async (file) => {
    if (!file) return;
    setEditUploading(true);
    try { const url = await uploadAsset(file); setEditTeam((p) => ({ ...p, logo: url })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEditUploading(false); }
  };
  const confirmDelete = async () => {
    await api.delete(`/teams/${editTeam.id}`);
    toast.success("Mannschaft gelöscht");
    setEditTeam(null);
  };

  // ---- standings editor ----
  const saveStandings = async () => {
    try {
      await api.put("/standings", { columns: cols, rows });
      toast.success("Tabelle gespeichert & live");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const setCell = (r, c, v) => {
    const next = rows.map((row) => [...row]);
    next[r][c] = v;
    setRows(next);
  };
  const setColName = (c, v) => {
    const next = [...cols];
    next[c] = v;
    setCols(next);
  };
  const addColumn = () => {
    setCols([...cols, `Spalte ${cols.length + 1}`]);
    setRows(rows.map((r) => [...r, ""]));
  };
  const removeColumn = (c) => {
    setCols(cols.filter((_, i) => i !== c));
    setRows(rows.map((r) => r.filter((_, i) => i !== c)));
  };
  const addRow = () => setRows([...rows, cols.map(() => "")]);
  const removeRow = (r) => setRows(rows.filter((_, i) => i !== r));

  // ---- fields (Spielfelder) ----
  const addField = async () => {
    if (!fieldName.trim()) return;
    try {
      await api.post("/fields", { name: fieldName.trim() });
      setFieldName("");
      toast.success("Spielfeld hinzugefügt");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const deleteField = async (id) => {
    await api.delete(`/fields/${id}`);
    toast.success("Spielfeld entfernt");
  };

  // ---- groups (Gruppen) ----
  const addGroup = async () => {
    if (!groupName.trim()) return;
    try {
      await api.post("/groups", { name: groupName.trim() });
      setGroupName("");
      toast.success("Gruppe hinzugefügt");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const deleteGroup = async (id) => {
    await api.delete(`/groups/${id}`);
    toast.success("Gruppe entfernt");
  };

  const updateFieldColor = async (f, color) => {
    await api.put(`/fields/${f.id}`, { name: f.name, color });
  };

  // ---- matchdays (Spieltage) ----
  const addMatchday = async () => {
    if (!mdName.trim()) return;
    try {
      await api.post("/matchdays", { name: mdName.trim(), date: mdDate || null });
      setMdName(""); setMdDate("");
      toast.success("Spieltag hinzugefügt");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const deleteMatchday = async (id) => {
    await api.delete(`/matchdays/${id}`);
    toast.success("Spieltag entfernt");
  };

  // team slot select: fixed team ("team:<name>") or placeholder ref ("ref:<gid>:<place>")
  const teamSelectValue = (m, side) =>
    m[`${side}_ref`] ? `ref:${m[`${side}_ref`].group_id}:${m[`${side}_ref`].place}` : `team:${m[`${side}_name`] || ""}`;
  const onTeamSelect = (m, side, val) => {
    if (val.startsWith("ref:")) {
      const [, gid, place] = val.split(":");
      updateMatch(m, { [`${side}_ref`]: { group_id: gid, place: Number(place) }, published: false });
    } else {
      updateMatch(m, { [`${side}_ref`]: null, [`${side}_name`]: val.slice(5) });
    }
  };
  const renderTeamOptions = (m, side) => (
    <>
      <optgroup label="Mannschaften">
        {teams.map((t) => <option key={t.id} value={`team:${t.name}`}>{t.name}</option>)}
        {!m[`${side}_ref`] && m[`${side}_name`] && !teams.some((t) => t.name === m[`${side}_name`]) && (
          <option value={`team:${m[`${side}_name`]}`}>{m[`${side}_name`]}</option>
        )}
      </optgroup>
      {groups.length > 0 && (
        <optgroup label="Platzierungen">
          {groups.flatMap((g) => [1, 2, 3, 4].map((p) => (
            <option key={`${g.id}-${p}`} value={`ref:${g.id}:${p}`}>{p}. {g.name}</option>
          )))}
        </optgroup>
      )}
    </>
  );

  // ---- matches ----
  const addMatch = async () => {
    if (teams.length < 2) {
      toast.error("Mindestens 2 Mannschaften anlegen");
      return;
    }
    await api.post("/matches", { team1_name: teams[0].name, team2_name: teams[1].name, status: "Geplant" });
    toast.success("Begegnung hinzugefügt");
  };
  const updateMatch = async (m, patch) => {
    await api.put(`/matches/${m.id}`, { ...m, ...patch });
  };
  const deleteMatch = async (id) => {
    await api.delete(`/matches/${id}`);
  };

  return (
    <div className="space-y-8">
      {/* Team management */}
      {view === "teams" && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-4">Mannschaftsverwaltung</h2>
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <input
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Teamname"
            data-testid="team-name-input"
            className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm flex-1 min-w-[180px]"
          />
          <input ref={logoInputRef} type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="team-logo-input" className="hidden" />
          <button
            onClick={() => logoInputRef.current?.click()}
            data-testid="team-logo-pick-btn"
            className="flex items-center gap-2 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 text-sm hover:border-[#37456b] transition-colors"
          >
            <ImageIcon className="w-4 h-4" /> {logoFile ? logoFile.name.slice(0, 18) : "Logo wählen"}
          </button>
          <button onClick={addTeam} disabled={uploading} data-testid="add-team-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            <Plus className="w-4 h-4" /> {uploading ? "…" : "Hinzufügen"}
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {teams.map((t) => (
            <div key={t.id} className="flex items-center gap-3 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-3 py-2" data-testid={`team-chip-${t.id}`}>
              {t.logo ? <img src={t.logo} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold text-black" style={{ background: t.color || "#00F0FF" }}>{t.short || t.name?.[0]}</div>}
              <span className="text-sm flex-1">{t.name}</span>
              <button onClick={() => openEdit(t)} data-testid={`team-edit-${t.id}`} className="text-[#8A99AD] hover:text-[#00F0FF] p-1" title="Bearbeiten"><Pencil className="w-4 h-4" /></button>
            </div>
          ))}
          {teams.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Mannschaften.</p>}
        </div>
        {editTeam && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="team-edit-modal">
            <div className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold uppercase tracking-wider text-lg">Mannschaft bearbeiten</h3>
                <button onClick={() => setEditTeam(null)} className="text-[#8A99AD] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <label className="block text-xs uppercase tracking-widest text-[#8A99AD] mb-1">Name</label>
              <input value={editTeam.name} onChange={(e) => setEditTeam((p) => ({ ...p, name: e.target.value }))} data-testid="team-edit-name" className="w-full bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm mb-4" />
              <label className="block text-xs uppercase tracking-widest text-[#8A99AD] mb-1">Kürzel (Badge ohne Logo, optional)</label>
              <input value={editTeam.short || ""} maxLength={5} placeholder="z. B. RKV" onChange={(e) => setEditTeam((p) => ({ ...p, short: e.target.value }))} data-testid="team-edit-short" className="w-full bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm mb-4" />
              <label className="block text-xs uppercase tracking-widest text-[#8A99AD] mb-1">Logo</label>
              <div className="flex items-center gap-3 mb-4">
                {editTeam.logo ? <img src={editTeam.logo} alt="" className="w-14 h-14 rounded object-cover" /> : <div className="w-14 h-14 rounded flex items-center justify-center text-base font-bold text-black" style={{ background: editTeam.color }}>{editTeam.short || editTeam.name?.[0]}</div>}
                <input ref={editLogoRef} type="file" accept="image/*" className="hidden" data-testid="team-edit-logo-input" onChange={(e) => editUploadLogo(e.target.files?.[0])} />
                <button onClick={() => editLogoRef.current?.click()} disabled={editUploading} data-testid="team-edit-logo-btn" className="flex items-center gap-2 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-3 py-2 text-sm hover:border-[#37456b] disabled:opacity-50"><Upload className="w-4 h-4" />{editUploading ? "…" : "Neues Logo"}</button>
                {editTeam.logo && <button onClick={() => setEditTeam((p) => ({ ...p, logo: null }))} data-testid="team-edit-logo-remove" className="flex items-center gap-2 text-[#8A99AD] hover:text-[#FF2A55] text-sm"><Trash2 className="w-4 h-4" />Entfernen</button>}
              </div>
              <label className="block text-xs uppercase tracking-widest text-[#8A99AD] mb-1">Farbe (Badge ohne Logo)</label>
              <div className="flex flex-wrap gap-2 mb-6">
                {FIELD_COLORS.map((c) => (
                  <button key={c} onClick={() => setEditTeam((p) => ({ ...p, color: c }))} data-testid={`team-edit-color-${c}`} className={`w-8 h-8 rounded-full border-2 ${editTeam.color === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center justify-between">
                {!teamConfirmDel ? (
                  <button onClick={() => setTeamConfirmDel(true)} data-testid="team-edit-delete" className="flex items-center gap-2 text-[#FF2A55] hover:text-white text-sm"><Trash2 className="w-4 h-4" />Löschen</button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#FF2A55]">Wirklich?</span>
                    <button onClick={confirmDelete} data-testid="team-edit-delete-confirm" className="bg-[#FF2A55] text-white text-sm px-3 py-1.5 rounded-lg">Ja, löschen</button>
                    <button onClick={() => setTeamConfirmDel(false)} className="text-[#8A99AD] text-sm">Abbrechen</button>
                  </div>
                )}
                <button onClick={saveEdit} data-testid="team-edit-save" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white"><Save className="w-4 h-4" />Speichern</button>
              </div>
            </div>
          </div>
        )}
      </section>
      )}

      {/* Standings editor */}
      {view === "standings" && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <label className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <span className="text-xs uppercase tracking-widest text-[#8A99AD]">Überschrift Punktetabelle (Display)</span>
            <input value={standingsTitle} onChange={(e) => setStandingsTitle(e.target.value)} onBlur={() => standingsTitle !== (snapshot.settings.standings_title || "Punktetabelle") && saveTitle({ standings_title: standingsTitle })} data-testid="standings-title-input"
              className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] font-display font-bold uppercase tracking-wider text-lg" />
          </label>
          <div className="flex gap-2">
            <button onClick={addColumn} data-testid="add-column-btn" className="flex items-center gap-1 text-sm bg-[#0A0C10] border border-[#1E2638] rounded-lg px-3 py-2 hover:border-[#37456b]">
              <Plus className="w-4 h-4" /> Spalte
            </button>
            <button onClick={addRow} data-testid="add-row-btn" className="flex items-center gap-1 text-sm bg-[#0A0C10] border border-[#1E2638] rounded-lg px-3 py-2 hover:border-[#37456b]">
              <Plus className="w-4 h-4" /> Zeile
            </button>
            <button onClick={saveStandings} data-testid="save-standings-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors">
              <Save className="w-4 h-4" /> Speichern
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="standings-editor">
            <thead>
              <tr>
                {cols.map((c, ci) => (
                  <th key={ci} className="p-1">
                    <div className="flex items-center gap-1">
                      <input
                        value={c}
                        onChange={(e) => setColName(ci, e.target.value)}
                        className="w-full bg-[#0A0C10] border border-[#1E2638] rounded px-2 py-1.5 text-center font-display uppercase tracking-wide text-[#E5FF00] outline-none focus:border-[#00F0FF]"
                      />
                      <button onClick={() => removeColumn(ci)} className="text-[#8A99AD] hover:text-[#FF2A55]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </th>
                ))}
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="p-1">
                      <input
                        value={cell}
                        onChange={(e) => setCell(ri, ci, e.target.value)}
                        data-testid={`cell-${ri}-${ci}`}
                        className="w-full bg-[#0A0C10] border border-[#1E2638] rounded px-2 py-1.5 text-center font-mono-num outline-none focus:border-[#00F0FF]"
                      />
                    </td>
                  ))}
                  <td className="p-1 text-center">
                    <button onClick={() => removeRow(ri)} className="text-[#8A99AD] hover:text-[#FF2A55]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {view === "standings" && groups.length > 0 && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-4">Gruppen-Ranglisten</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((g) => <GroupStandings key={g.id} group={g} data={groupStandings[g.id]} />)}
        </div>
      </section>
      )}

      {/* Spielfelder */}
      {view === "schedule" && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#00F0FF]" /> Spielfelder</h2>
        <p className="text-sm text-[#8A99AD] mb-4">Felder definieren und im Spielplan je Begegnung auswählen.</p>
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <input
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addField()}
            placeholder="z. B. Feld 1, Halle Nord"
            data-testid="field-name-input"
            className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm flex-1 min-w-[180px]"
          />
          <button onClick={addField} data-testid="add-field-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors">
            <Plus className="w-4 h-4" /> Hinzufügen
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {fields.map((f) => (
            <div key={f.id} className="flex items-center gap-2 bg-[#0A0C10] border rounded-lg pl-2.5 pr-2 py-1.5" style={{ borderColor: f.color || "#1E2638" }} data-testid={`field-chip-${f.id}`}>
              <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: f.color || "#00F0FF" }} />
              <span className="text-sm">{f.name}</span>
              <div className="flex items-center gap-1 mx-1">
                {FIELD_COLORS.map((c) => (
                  <button key={c} onClick={() => updateFieldColor(f, c)} title={c} data-testid={`field-color-${f.id}-${c}`}
                    className="w-3.5 h-3.5 rounded-full" style={{ background: c, boxShadow: (f.color || "#00F0FF") === c ? "0 0 0 2px #fff" : "0 0 0 1px rgba(0,0,0,0.4)" }} />
                ))}
              </div>
              <button onClick={() => deleteField(f.id)} className="text-[#8A99AD] hover:text-[#FF2A55]">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {fields.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Spielfelder.</p>}
        </div>
      </section>
      )}

      {/* Gruppen */}
      {view === "schedule" && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1 flex items-center gap-2"><Layers className="w-5 h-5 text-[#00F0FF]" /> Gruppen</h2>
        <p className="text-sm text-[#8A99AD] mb-4">Gruppen anlegen (z. B. Vorrunde A) und im Spielplan zuordnen. Ranglisten werden automatisch berechnet – bei Gleichstand per Drag anpassbar.</p>
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <input value={groupName} onChange={(e) => setGroupName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGroup()} placeholder="z. B. Vorrunde A" data-testid="group-name-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm flex-1 min-w-[180px]" />
          <button onClick={addGroup} data-testid="add-group-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors"><Plus className="w-4 h-4" /> Hinzufügen</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => (
            <div key={g.id} className="flex items-center gap-2 bg-[#0A0C10] border border-[#1E2638] rounded-lg pl-3 pr-2 py-1.5" data-testid={`group-chip-${g.id}`}>
              <Layers className="w-4 h-4 text-[#8A99AD]" />
              <span className="text-sm">{g.name}</span>
              <button onClick={() => deleteGroup(g.id)} className="text-[#8A99AD] hover:text-[#FF2A55]"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {groups.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Gruppen.</p>}
        </div>
      </section>
      )}

      {/* Spieltage */}
      {view === "schedule" && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#00F0FF]" /> Spieltage</h2>
        <p className="text-sm text-[#8A99AD] mb-4">Spieltage anlegen (z. B. Samstag / Sonntag) und im Spielplan zuordnen. Auf dem Display rotiert der Spielplan pro Spieltag.</p>
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <input value={mdName} onChange={(e) => setMdName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMatchday()} placeholder="z. B. Samstag" data-testid="matchday-name-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm flex-1 min-w-[160px]" />
          <input type="date" value={mdDate} onChange={(e) => setMdDate(e.target.value)} data-testid="matchday-date-input" className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm" />
          <button onClick={addMatchday} data-testid="add-matchday-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors"><Plus className="w-4 h-4" /> Hinzufügen</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {matchdays.map((md) => (
            <div key={md.id} className="flex items-center gap-2 bg-[#0A0C10] border border-[#1E2638] rounded-lg pl-3 pr-2 py-1.5" data-testid={`matchday-chip-${md.id}`}>
              <CalendarDays className="w-4 h-4 text-[#8A99AD]" />
              <span className="text-sm">{md.name}{md.date ? ` · ${md.date}` : ""}</span>
              <button onClick={() => deleteMatchday(md.id)} className="text-[#8A99AD] hover:text-[#FF2A55]"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {matchdays.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Spieltage.</p>}
        </div>
      </section>
      )}

      {/* Match schedule */}
      {view === "schedule" && (
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <label className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <span className="text-xs uppercase tracking-widest text-[#8A99AD]">Überschrift Spielplan (Display)</span>
            <input value={scheduleTitle} onChange={(e) => setScheduleTitle(e.target.value)} onBlur={() => scheduleTitle !== (snapshot.settings.schedule_title || "Spielplan") && saveTitle({ schedule_title: scheduleTitle })} data-testid="schedule-title-input"
              className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] font-display font-bold uppercase tracking-wider text-lg" />
          </label>
          <button onClick={addMatch} data-testid="add-match-btn" className="flex items-center gap-1 text-sm bg-[#0A0C10] border border-[#1E2638] rounded-lg px-3 py-2 hover:border-[#37456b]">
            <Plus className="w-4 h-4" /> Begegnung
          </button>
        </div>
        <div className="space-y-2">
          {matches.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center gap-2 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-3 py-2" data-testid={`match-row-${m.id}`}>
              <input type="time" key={m.start_time || "empty"} defaultValue={m.start_time || ""} onBlur={(e) => e.target.value !== (m.start_time || "") && updateMatch(m, { start_time: e.target.value })} data-testid={`match-time-${m.id}`} className="bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-sm outline-none font-mono-num w-28" />
              <select value={m.field || ""} onChange={(e) => updateMatch(m, { field: e.target.value || null })} data-testid={`match-field-${m.id}`} className="bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-sm outline-none w-28">
                <option value="">— Feld —</option>
                {fields.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
                {m.field && !fields.some((f) => f.name === m.field) && <option value={m.field}>{m.field}</option>}
              </select>
              <select value={m.group_id || ""} onChange={(e) => updateMatch(m, { group_id: e.target.value || null })} data-testid={`match-group-${m.id}`} className="bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-sm outline-none w-28">
                <option value="">— Gruppe —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <select value={m.matchday_id || ""} onChange={(e) => updateMatch(m, { matchday_id: e.target.value || null })} data-testid={`match-matchday-${m.id}`} className="bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-sm outline-none w-28">
                <option value="">— Spieltag —</option>
                {matchdays.map((md) => <option key={md.id} value={md.id}>{md.name}</option>)}
              </select>
              <select value={teamSelectValue(m, "team1")} onChange={(e) => onTeamSelect(m, "team1", e.target.value)} data-testid={`match-team1-${m.id}`} className="bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-sm outline-none flex-1 min-w-[140px]">
                {renderTeamOptions(m, "team1")}
              </select>
              <input type="number" value={m.score1 ?? ""} onChange={(e) => updateMatch(m, { score1: e.target.value === "" ? null : Number(e.target.value) })} className="w-14 bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-center font-mono-num text-sm outline-none" />
              <span className="text-[#8A99AD]">:</span>
              <input type="number" value={m.score2 ?? ""} onChange={(e) => updateMatch(m, { score2: e.target.value === "" ? null : Number(e.target.value) })} className="w-14 bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-center font-mono-num text-sm outline-none" />
              <select value={teamSelectValue(m, "team2")} onChange={(e) => onTeamSelect(m, "team2", e.target.value)} data-testid={`match-team2-${m.id}`} className="bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-sm outline-none flex-1 min-w-[140px]">
                {renderTeamOptions(m, "team2")}
              </select>
              <select value={m.status} onChange={(e) => updateMatch(m, { status: e.target.value })} className="bg-[#121620] border border-[#1E2638] rounded px-2 py-1.5 text-sm outline-none">
                <option>Geplant</option>
                <option>Läuft</option>
                <option>Beendet</option>
              </select>
              <button onClick={() => deleteMatch(m.id)} className="text-[#8A99AD] hover:text-[#FF2A55] p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {matches.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Begegnungen.</p>}
        </div>
      </section>
      )}
    </div>
  );
}
